<template>
    <div class="personal-investments-wrapper">
        <v-card class="personal-investments elevation-2">
            <v-card-title class="primary--text pb-2">
                <div class="d-flex align-center w-100">
                    <v-icon left color="primary" size="28">mdi-wallet</v-icon>
                    <div class="flex-grow-1">
                        <div class="text-h5 font-weight-bold">Moje dary</div>
                        <div class="text-caption grey--text">Prehľad všetkých mojich darovacích zmlúv</div>
                    </div>
                    <div class="d-flex align-center">
                        <v-chip v-if="investments.length > 0" color="primary" outlined class="mr-3">
                            <v-icon left small>mdi-file-document</v-icon>
                            {{ investments.length }} {{ contractText }}
                        </v-chip>
                    </div>
                </div>
            </v-card-title>

            <!-- Empty state -->
            <div v-if="investments.length === 0 && !loading" class="empty-state">
                <v-row justify="center">
                    <v-col cols="12" md="8" lg="6">
                        <div class="text-center py-12">
                            <div class="empty-icon mb-6">
                                <v-icon size="120" color="grey lighten-2">mdi-briefcase-outline</v-icon>
                            </div>
                            <div class="text-h5 grey--text text--darken-1 mb-3">Zatiaľ nemáš uzatvorenú žiadnu platnú
                                zmluvu
                                o
                                darovaní</div>
                            <div class="text-body-1 grey--text mb-6">
                                Začni darovať a staň sa súčasťou nášho úspechu.
                            </div>
                            <v-btn large color="primary" @click="$emit('start-investing')" class="mb-3">
                                <v-icon left>mdi-rocket-launch</v-icon>
                                Začať darovať
                            </v-btn>
                            <!-- <div class="text-caption grey--text">
                                Minimálna investícia už od 1€
                            </div> -->
                        </div>
                    </v-col>
                </v-row>
            </div>

            <!-- Main content with investments -->
            <v-card-text v-else class="pb-2">

                <!-- Investments table -->
                <v-row>
                    <v-col cols="12">
                        <v-card outlined>
                            <v-card-subtitle class="primary--text font-weight-bold">
                                Detail mojich darovacích zmlúv
                            </v-card-subtitle>
                            <v-card-text>
                                <v-data-table :headers="investmentHeaders" :items="enrichedInvestments"
                                    :loading="loading" class="elevation-0" hide-default-footer :items-per-page="-1"
                                    :sort-by="['created_at']" :sort-desc="[true]">
                                    <template v-slot:item.contract_number="{ item }">
                                        <div class="d-flex align-center">
                                            <v-chip small color="primary" outlined class="mr-2">
                                                {{ item.contract_number }}
                                            </v-chip>
                                        </div>
                                    </template>

                                    <template v-slot:item.status="{ item }">
                                        <v-chip small :color="getStatusColor(item.status)" dark
                                            class="font-weight-medium"
                                            :class="{ 'cursor-pointer': item.status === false || item.status === 'pending' }"
                                            @click="item.status === false || item.status === 'pending' ? openPaymentDialog(item) : null">
                                            <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                                            {{ getStatusText(item.status) }}
                                            <v-icon v-if="item.status === false || item.status === 'pending'" right
                                                x-small>mdi-information-outline</v-icon>
                                        </v-chip>
                                    </template>

                                    <template v-slot:item.investment_amount="{ item }">
                                        <span class="font-weight-bold text-body-1">
                                            {{ formatCurrency(item.investment_amount) }}
                                        </span>
                                    </template>

                                    <template v-slot:item.coefficient="{ item }">
                                        <v-chip small :color="getCoefficientColor(item.coefficient)" dark>
                                            {{ item.coefficient }}x
                                        </v-chip>
                                    </template>

                                    <template v-slot:item.payout_claim="{ item }">
                                        <span class="success--text font-weight-bold">
                                            {{ formatCurrency(item.payout_claim) }}
                                        </span>
                                    </template>

                                    <template v-slot:item.annual_payout="{ item }">
                                        <span class="info--text font-weight-medium">
                                            {{ formatCurrency(item.annual_payout) }}
                                        </span>
                                    </template>

                                    <template v-slot:item.recovery_balance="{ item }">
                                        <div :class="item.recovery_balance >= 0 ? 'success--text' : 'error--text'">
                                            {{ item.recovery_balance >= 0 ? '+' : '' }}{{
                                                formatCurrency(item.recovery_balance) }}
                                        </div>
                                        <div class="text-caption grey--text">
                                            {{ item.recovery_balance >= 0 ? 'Dar vrátený' : 'Chýba do vrátenia'
                                            }}
                                        </div>
                                    </template>

                                    <template v-slot:item.benefit_balance="{ item }">
                                        <div :class="item.benefit_balance >= 0 ? 'success--text' : 'error--text'">
                                            {{ item.benefit_balance >= 0 ? '+' : '' }}{{
                                                formatCurrency(item.benefit_balance) }}
                                        </div>
                                        <div class="text-caption grey--text">
                                            {{ item.benefit_balance >= 0 ? 'Benefit dosiahnutý' : 'Chýba do benefitu' }}
                                        </div>
                                    </template>

                                    <template v-slot:item.created_at="{ item }">
                                        <div>
                                            <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
                                            <div v-if="item.payment_date" class="text-caption success--text">
                                                Uhradené: {{ formatDate(item.payment_date) }}
                                            </div>
                                        </div>
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-menu offset-y>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon small v-bind="attrs" v-on="on">
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <!-- <v-list-item @click="showContractDetail(item)">
                                                <v-list-item-icon>
                                                    <v-icon small>mdi-eye</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>Detail</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item> -->
                                                <v-list-item @click="downloadContract(item)">
                                                    <v-list-item-icon>
                                                        <v-icon small>mdi-download</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Stiahnuť PDF</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item v-if="canDeleteInvestment(item)"
                                                    :disabled="isDeletingInvestment(item.id)"
                                                    @click="requestDeleteInvestment(item)">
                                                    <v-list-item-icon>
                                                        <v-icon small color="error">
                                                            {{ isDeletingInvestment(item.id) ? 'mdi-loading mdi-spin' :
                                                                'mdi-delete' }}
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title class="error--text">
                                                            {{ isDeletingInvestment(item.id) ? 'Mazanie...' :
                                                                'Vymazať dar' }}
                                                        </v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </template>

                                    <template v-slot:header.payout_claim="{ header }">
                                        <div class="d-flex align-center justify-end w-100">
                                            {{ header.text }}
                                            <v-tooltip
                                                :right="!$vuetify.breakpoint.smAndDown"
                                                :bottom="$vuetify.breakpoint.smAndDown"
                                                :open-on-hover="!$vuetify.breakpoint.smAndDown"
                                                :open-on-click="$vuetify.breakpoint.smAndDown"
                                            >
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon
                                                        x-small
                                                        class="ml-1"
                                                        color="grey"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        :aria-label="`Info: ${header.text}`"
                                                        style="opacity: 0.6; cursor: help;"
                                                    >
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>Možný nárok komunitného benefitu v objeme vyčíslenom v EURO</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Summary row -->
                <v-row class="mt-4">
                    <v-col cols="12">
                        <v-card outlined color="info lighten-5">
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="3">
                                        <div class="text-center">
                                            <div class="text-caption">Celkovo moje dary spolu</div>
                                            <div class="text-h5 primary--text font-weight-bold">
                                                {{ formatCurrency(totalMyInvestment) }}
                                            </div>
                                        </div>
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <div class="text-center">
                                            <div class="text-caption">Celkovo s možným benefitom</div>
                                            <div class="text-h5 success--text font-weight-bold">
                                                {{ formatCurrency(totalMyPayoutClaim) }}
                                            </div>
                                        </div>
                                    </v-col>
                                    <!-- <v-col cols="12" md="3">
                                        <div class="text-center">
                                            <div class="text-caption">Očakávaná ročná výplata</div>
                                            <div class="text-h5 info--text font-weight-bold">
                                                {{ formatCurrency(totalAnnualPayout) }}
                                            </div>
                                        </div>
                                    </v-col> -->
                                    <v-col cols="12" md="3">
                                        <div class="text-center">
                                            <div class="text-caption">Môj podiel %</div>
                                            <div class="text-h5 warning--text font-weight-bold">
                                                {{ myParticipationPercentage }}%
                                            </div>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Quick additional investment -->
                <!-- <v-row class="mt-6">
                <v-col cols="12">
                    <v-card outlined class="quick-invest-card">
                        <v-card-title class="text-h6 success--text pb-2">
                            <v-icon left color="success">mdi-flash</v-icon>
                            Ďalšia prípadná investícia
                        </v-card-title>
                        <v-card-text>
                            <v-row align="center">
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="additionalInvestment"
                                        label="Chcem ešte investovať sumu" type="number" step="10" min="10" suffix="€"
                                        outlined dense hide-details="auto" :rules="investmentRules"
                                        @focus="$event.target.select()">
                                        <template v-slot:prepend-inner>
                                            <v-icon color="success">mdi-plus</v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="text-center">
                                        <div class="text-caption">Očakávaný nárok s koeficientom {{
                                            getProjectedCoefficient
                                        }}x</div>
                                        <div class="text-h5 success--text font-weight-bold">
                                            {{ formatCurrency(additionalPayoutClaim) }}
                                        </div>
                                        <div class="text-caption grey--text">
                                            ROI: {{ additionalROI }}% ročne
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-btn large color="success" block :disabled="!isValidAdditionalInvestment"
                                        :loading="investingAdditional" @click="investAdditional">
                                        <v-icon left>mdi-rocket-launch</v-icon>
                                        Investovať ešte
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row> -->
            </v-card-text>
        </v-card>

        <!-- Payment Details Dialog -->
        <payment-details-dialog v-model="paymentDialog" :amount="paymentDialogAmount"
            :variable-symbol="currentUser && currentUser.id ? currentUser.id : null" />
    </div>
</template>

<script>
import { APIService } from "@/http/APIService.js";
import { MIN_INVESTMENT } from '@/config/investment';
import {
    COEFFICIENT_LEVELS,
    calculateInvestmentBreakdown,
    calculatePayoutClaim,
    formatCurrency
} from '@/utils/investment-math';
import PaymentDetailsDialog from '@/components/invest/PaymentDetailsDialog.vue';

const apiService = new APIService();


export default {
    name: "PersonalInvestmentsTable",

    components: {
        PaymentDetailsDialog
    },

    props: {
        investments: {
            type: Array,
            default: () => []
        },
        totalInvestments: {
            type: Number,
            default: 0
        },
        paidInvestmentsTotal: {
            type: Number,
            default: 0
        },
        annualRevenue: {
            type: Number,
            default: 0
        },
        payoutPercentage: {
            type: Number,
            default: 60
        },
        loading: {
            type: Boolean,
            default: false
        },
        currentUser: {
            type: Object,
            default: () => ({})
        },
        isStaff: {
            type: Boolean,
            default: false
        },
        deletingInvestmentIds: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            showDashboardDetails: false,
            modeledAnnualRevenue: this.annualRevenue,
            additionalInvestment: 1000,
            investingAdditional: false,
            paymentDialog: false,
            paymentDialogAmount: null,

            investmentHeaders: [
                { text: 'Dátum uzavretia', value: 'created_at', width: '120px' },
                { text: 'Číslo zmluvy', value: 'contract_number', width: '130px' },
                { text: 'Status', value: 'status', width: '100px' },
                { text: 'Koef.', value: 'coefficient', width: '80px', align: 'center' },
                { text: 'Dar', value: 'investment_amount', align: 'right' },
                { text: 'Nárok', value: 'payout_claim', align: 'end' },
                // { text: 'Ročná výplata', value: 'annual_payout', align: 'right' },
                // { text: 'Stav investície', value: 'recovery_balance', align: 'right' },
                // { text: 'Stav benefitu', value: 'benefit_balance', align: 'right' },
                { text: 'Akcie', value: 'actions', sortable: false, width: '80px' }
            ]
        };
    },

    computed: {
        contractText() {
            if (this.investments.length === 1) return 'zmluva';
            if (this.investments.length < 5) return 'zmluvy';
            return 'zmlúv';
        },

        // Obohať investície o vypočítané hodnoty
        enrichedInvestments() {
            return this.investments.map(investment => {
                const annualPayout = this.calculateAnnualPayout(investment);
                const recoveryBalance = annualPayout - investment.investment_amount;
                const benefitBalance = annualPayout - investment.payout_claim;

                return {
                    ...investment,
                    coefficient: this.normalizeCoefficient(investment.coefficient),
                    annual_payout: annualPayout,
                    recovery_balance: recoveryBalance,
                    benefit_balance: benefitBalance
                };
            });
        },

        totalMyPaidInvestment() {
            return this.investments.reduce((sum, investment) => {
                if (!this.isInvestmentPaid(investment)) {
                    return sum;
                }

                const amount = Number(investment.investment_amount);
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        },

        totalMyInvestment() {
            return this.investments.reduce((sum, inv) => {
                const amount = Number(inv.investment_amount);
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        },

        totalMyPayoutClaim() {
            return this.investments.reduce((sum, inv) => {
                const amount = Number(inv.payout_claim);
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        },

        totalAnnualPayout() {
            return this.enrichedInvestments.reduce((sum, inv) => sum + inv.annual_payout, 0);
        },

        myParticipationPercentage() {
            // Keep participation aligned with payout denominator (paid/active investments).
            const totalAllPaidInvestments = this.effectivePayoutBaseInvestments;
            if (!totalAllPaidInvestments) return '0.0000';
            return ((this.totalMyPaidInvestment / totalAllPaidInvestments) * 100).toFixed(4);
        },

        keyMetrics() {
            return [
                {
                    label: 'Celkový dar',
                    value: this.formatCurrency(this.totalMyInvestment),
                    color: 'primary',
                    progress: (this.totalMyInvestment / 100000) * 100 // Ukážková progress
                },
                {
                    label: 'Celkový nárok',
                    value: this.formatCurrency(this.totalMyPayoutClaim),
                    color: 'success',
                    subtitle: 'S benefitom'
                },
                {
                    label: 'Ročná výplata',
                    value: this.formatCurrency(this.totalAnnualPayout),
                    color: 'info',
                    subtitle: 'Očakávaná'
                },
                {
                    label: 'ROI',
                    value: this.totalMyInvestment > 0 ?
                        ((this.totalAnnualPayout / this.totalMyInvestment) * 100).toFixed(1) + '%' : '0%',
                    color: 'warning',
                    subtitle: 'Ročne'
                }
            ];
        },

        investmentRecoveryProgress() {
            if (this.totalMyInvestment === 0) return 0;
            return Math.min(100, (this.totalAnnualPayout / this.totalMyInvestment) * 100);
        },

        benefitProgress() {
            if (this.totalMyPayoutClaim === 0) return 0;
            return Math.min(100, (this.totalAnnualPayout / this.totalMyPayoutClaim) * 100);
        },

        effectivePayoutBaseInvestments() {
            const paidTotal = Number(this.paidInvestmentsTotal);
            if (Number.isFinite(paidTotal) && paidTotal > 0) {
                return paidTotal;
            }

            const total = Number(this.totalInvestments);
            return Number.isFinite(total) && total > 0 ? total : 0;
        },

        // Ďalšia investícia
        investmentRules() {
            return [
                v => !!v || 'Suma je povinná',
                v => v >= 1 || 'Minimálny dar je 1€',
                v => v % 1 === 0 || 'Len násobky 1€',
                v => v <= 10 || 'Maximum 10€'
            ];
        },

        isValidAdditionalInvestment() {
            return this.additionalInvestment >= 10 &&
                this.additionalInvestment % 10 === 0 &&
                this.additionalInvestment <= 1000000;
        },

        getProjectedCoefficient() {
            // New logic using shared utility
            // Assuming currentTotal is fixed at 2687420 as per original hardcoded value
            const currentTotal = this.totalInvestments;

            // If the investment spans levels, this "flat" coefficient might be inaccurate ("Mix"),
            // but for display purposes if we want a single number we can take the first level hit or average?
            // The original logic returned the FIRST level that covers the total.
            // i.e. currentTotal < threshold.

            for (let level of COEFFICIENT_LEVELS) {
                if (currentTotal < level.threshold) {
                    return level.coefficient;
                }
            }
            return 1.5;
        },

        additionalPayoutClaim() {
            // More accurate calculation using breakdown
            const currentTotal = this.totalInvestments;
            const breakdown = calculateInvestmentBreakdown(
                this.additionalInvestment,
                currentTotal,
                COEFFICIENT_LEVELS
            );
            return calculatePayoutClaim(breakdown);
        },

        additionalROI() {
            if (this.additionalInvestment === 0) return 0;
            const projectedAnnualPayout = this.calculateProjectedPayout(this.additionalInvestment);
            return ((projectedAnnualPayout / this.additionalInvestment) * 100).toFixed(1);
        }
    },

    watch: {
        annualRevenue(newVal) {
            this.modeledAnnualRevenue = newVal;
        }
    },

    methods: {
        formatCurrency,

        parseNumericValue(value) {
            if (typeof value === 'number') {
                return Number.isFinite(value) ? value : 0;
            }

            if (typeof value === 'string') {
                const normalized = value
                    .replace(/\s/g, '')
                    .replace('€', '')
                    .replace(',', '.');
                const parsed = Number(normalized);
                return Number.isFinite(parsed) ? parsed : 0;
            }

            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        },

        calculateAnnualPayout(investment) {
            // Ročná výplata = "Mne sa z toho vyplatí" (projekcia podľa podielu investície na celku).
            if (!this.isInvestmentPaid(investment)) {
                return 0;
            }

            const minimumInvestmentAmount = Number(MIN_INVESTMENT);
            if (!Number.isFinite(minimumInvestmentAmount) || minimumInvestmentAmount <= 0) {
                return 0;
            }

            const modeledRevenue = this.parseNumericValue(this.modeledAnnualRevenue);
            const revenueForPayouts = modeledRevenue * (this.payoutPercentage / 100);
            const totalAllPaidInvestments = this.effectivePayoutBaseInvestments;

            if (!Number.isFinite(totalAllPaidInvestments) || totalAllPaidInvestments <= 0) {
                return 0;
            }

            const payoutShareCount = totalAllPaidInvestments / minimumInvestmentAmount;
            if (!Number.isFinite(payoutShareCount) || payoutShareCount <= 0) {
                return 0;
            }

            const investmentAmount = this.parseNumericValue(investment.investment_amount);
            if (!Number.isFinite(investmentAmount) || investmentAmount <= 0) {
                return 0;
            }

            const investmentShareCount = investmentAmount / minimumInvestmentAmount;
            const annualPayoutPerShare = revenueForPayouts / payoutShareCount;
            const rawAnnualPayout = annualPayoutPerShare * investmentShareCount;

            // Maximum annual payout is payout claim (narok).
            const payoutClaim = this.parseNumericValue(investment.payout_claim);
            const maxByBenefit = payoutClaim > 0 ? payoutClaim : Infinity;

            return Math.min(rawAnnualPayout, maxByBenefit);
        },

        calculateProjectedPayout(amount) {
            const modeledRevenue = this.parseNumericValue(this.modeledAnnualRevenue);
            const revenueForPayouts = modeledRevenue * (this.payoutPercentage / 100);
            const parsedAmount = this.parseNumericValue(amount);
            const totalAllInvestments = this.parseNumericValue(this.totalInvestments) + parsedAmount;
            return totalAllInvestments ? revenueForPayouts * (parsedAmount / totalAllInvestments) : 0;
        },

        updateModeledRevenue() {
            // Trigger recalculation
            this.$forceUpdate();
        },

        toggleDashboardDetails() {
            this.showDashboardDetails = !this.showDashboardDetails;
        },

        openPaymentDialog(investment) {
            this.paymentDialogAmount = investment.investment_amount || null;
            this.paymentDialog = true;
        },

        getStatusColor(status) {
            const colors = {
                false: 'warning',
                true: 'success',
                'completed': 'info',
                'cancelled': 'error'
            };
            return colors[status] || 'grey';
        },

        getStatusIcon(status) {
            const icons = {
                false: 'mdi-clock',
                true: 'mdi-check-circle',
                'completed': 'mdi-trophy',
                'cancelled': 'mdi-close-circle'
            };
            return icons[status] || 'mdi-help-circle';
        },

        getStatusText(status) {
            const texts = {
                false: 'Čaká na úhradu',
                true: 'Aktívna',
                'completed': 'Dokončená',
                'cancelled': 'Zrušená'
            };
            return texts[status] || 'Neznámy';
        },

        getCoefficientColor(coefficient) {
            if (coefficient >= 5) return 'success';
            if (coefficient >= 4) return 'warning';
            if (coefficient >= 3) return 'orange';
            if (coefficient >= 2) return 'error';
            return 'grey';
        },

        normalizeCoefficient(value) {
            const parsed = Number(value);
            if (!Number.isFinite(parsed)) {
                return 1.5;
            }

            return Math.max(1.5, parsed);
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('sk-SK');
        },



        showContractDetail(investment) {
            // Emit event to parent to show contract detail
            this.$emit('show-contract', investment);
        },

        downloadContract(investment) {
            apiService.downloadItem(`/investment/${investment.id}/download-contract/`, `darovacia_dohoda_${investment.id}.pdf`)
        },

        exportData() {
            // Export functionality
            const data = this.enrichedInvestments.map(inv => ({
                'Dátum': this.formatDate(inv.created_at),
                'Číslo zmluvy': inv.contract_number,
                'Dar': inv.investment_amount,
                'Koeficient': inv.coefficient,
                'Nárok': inv.payout_claim,
                'Ročná výplata': inv.annual_payout,
                'Status': this.getStatusText(inv.status)
            }));

            // Simple CSV export
            const csv = this.convertToCSV(data);
            this.downloadCSV(csv, 'moje_dary.csv');
        },

        convertToCSV(data) {
            if (!data.length) return '';

            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
            ].join('\n');

            return csvContent;
        },

        downloadCSV(csv, filename) {
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');

            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        },

        refreshData() {
            this.$emit('refresh');
        },

        isDeletingInvestment(investmentId) {
            return this.deletingInvestmentIds.includes(investmentId);
        },

        getInvestmentUserId(investment) {
            if (investment.user && typeof investment.user === 'object') {
                return investment.user.id;
            }
            return investment.user;
        },

        isInvestmentActive(investment) {
            const status = investment && investment.status;
            return status === true || status === 1 || status === '1' || status === 'true';
        },

        isInvestmentPaid(investment) {
            const status = String(investment && investment.status || '').toLowerCase();
            const paidStatus = status === 'completed' || status === 'active' || status === 'paid';
            return this.isInvestmentActive(investment) || paidStatus || !!investment.payment_date;
        },

        canDeleteInvestment(investment) {
            const currentUser = this.currentUser || {};
            const isAdmin = this.isStaff || currentUser.is_staff || currentUser.is_superuser;
            if (isAdmin) {
                return true;
            }

            const investmentOwnerId = this.getInvestmentUserId(investment);
            const currentUserId = currentUser.id;

            if (investmentOwnerId == null || currentUserId == null) {
                return false;
            }

            const isOwner = String(investmentOwnerId) === String(currentUserId);

            return isOwner && !this.isInvestmentActive(investment);
        },

        requestDeleteInvestment(investment) {
            if (!this.canDeleteInvestment(investment) || this.isDeletingInvestment(investment.id)) {
                return;
            }
            this.$emit('delete-investment', investment);
        },

        investAdditional() {
            if (!this.isValidAdditionalInvestment) return;

            this.investingAdditional = true;

            // Simulate API call
            setTimeout(() => {
                this.$emit('invest-additional', this.additionalInvestment);
                this.investingAdditional = false;
                this.additionalInvestment = 1000; // Reset
            }, 1000);
        }
    }
};
</script>

<style scoped>
.personal-investments {
    margin-bottom: 24px;
}

.empty-state {
    min-height: 400px;
    display: flex;
    align-items: center;
}

.dashboard-card {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.metric-card {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.metric-card:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
}

.metric-value {
    font-size: 1.8rem !important;
}

.quick-invest-card {
    border: 2px dashed #4caf50;
    background-color: rgba(76, 175, 80, 0.05);
}

.v-data-table>>>.v-data-table__wrapper {
    border-radius: 8px;
}

.cursor-pointer {
    cursor: pointer;
}

.cursor-pointer:hover {
    opacity: 0.85;
}

@media (max-width: 600px) {
    .metric-value {
        font-size: 1.4rem !important;
    }

    .text-h5 {
        font-size: 1.3rem !important;
    }
}
</style>